import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import { HUNDRED_PERCENT } from "@src/utils/formatters";
import { action, computed, makeObservable } from "mobx";

export class NumberInputPercentStore implements BaseStore {
  appStore: AppStore;

  private onNumberInputChange: (value: string) => void;
  private onPercentChange: (value: string | number) => void;
  private lastNonZeroSign = 1;

  constructor(
    appStore: AppStore,
    options: {
      onNumberInputChange: (value: string) => void;
      onPercentChange: (value: string | number) => void;
    },
  ) {
    this.appStore = appStore;
    this.onNumberInputChange = options.onNumberInputChange;
    this.onPercentChange = options.onPercentChange;
    makeObservable(this);
  }

  @computed
  get isEnabled() {
    return (
      this.appStore.workspaceStore.settings?.financial_amount_by_percent ??
      false
    );
  }

  @action.bound
  handleNumberChange(value: string | number, availableValue: number) {
    this.updateLastNonZeroSign(value);

    if (this.isEnabled && availableValue > 0) {
      // Update the percent value field
      const percentage =
        (Math.abs(Number(value)) / availableValue) * HUNDRED_PERCENT;
      this.onPercentChange(percentage);
    }

    this.onNumberInputChange(String(value));
  }

  @action.bound
  updatePercentFromNumberValue(value: number, availableValue: number) {
    const percentage =
      this.isEnabled && availableValue > 0
        ? (Math.abs(value) / availableValue) * HUNDRED_PERCENT
        : 0;

    this.onPercentChange(percentage);
  }

  @action.bound
  handlePercentChange(
    percentValue: string | number,
    availableValue: number,
    currentValue: string | number,
  ) {
    this.updateLastNonZeroSign(currentValue);

    const percent = Number(percentValue) || 0;
    const currentNumericValue = Number(currentValue);
    const sign =
      currentNumericValue === 0
        ? this.lastNonZeroSign
        : Math.sign(currentNumericValue);
    const value = sign * (percent / HUNDRED_PERCENT) * availableValue;

    this.updateLastNonZeroSign(value);
    this.onNumberInputChange(String(value));
    this.onPercentChange(percent);
  }

  private updateLastNonZeroSign(value: string | number) {
    const numericValue = Number(value);

    if (numericValue !== 0) {
      this.lastNonZeroSign = Math.sign(numericValue);
    }
  }
}
